import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getFeaturePropertyRows,
  getProductPropertyAssociationRows,
  updateFeaturePropertyAssociation,
} from '../../services/DataHelperFunctions';
import { ProductAssociationApi } from '../../api/ProductAssociationApi';
import { getImages } from './ImagesSlice';
import { getSalesText } from './SalesTextSlice';
import {
  getProperties,
  getPropertyAssociations,
  getFeaturePropertyAssociations,
  getAllProperties,
  getEnumListOfValues,
} from './PropertySlice';
import { getPrice, getPriceListMap } from './PriceSlice';
import { getCountryList, getMarkets, getMarketAssociation } from './MarketSlice';
import { getLanguageAssociation, getLanguages } from './LanguageSlice';
import { compareProductHierarchy, getProductHierarchy } from './ProductHierarchySlice';
import { EDataMaintenanceType, EDataTableType } from '../../data/Constants';
import { updateTableData } from '../../store/utilities/SliceDataHelper';

const initialState = {
  data: [],
  salesText: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  images: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  productHierarchy: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  compareProductHierarchy: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  prices: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  languageAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  languages: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  priceListMap: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  productCatalog: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  productConfiguration: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  archivedConfiguration: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  markets: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  marketAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  countries: [],
  uploadFile: {
    file: {},
    importDropzone: null,
  },
  chunkUpload: {
    type: '',
    fileName: '',
    fileId: '',
    isUploaded: false,
    uploadValue: 0,
    isUploadProgress: false,
  },
  properties: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  propertyFields: {
    types: [],
    applicabilities: [],
  },
  propertyAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  featurePropertyAssociation: {
    data: {},
    totalCount: 0,
    currentPage: 1,
    recordsPerPage: 15,
    searchKey: '',
    searchData: {},
  },
  propertyList: [],
  enumListOfValues: [],
};

//To get Product Catalog paginated data
export const getProductCatalog = createAsyncThunk(
  'dataMaintenance/getProductCatalog',
  async (action: { page: number; limit: number; searchCode?: string }, thunkAPI) => {
    return ProductAssociationApi.getProductCatalog(action, thunkAPI.dispatch);
  }
);

//To get Product Configuration paginated data
export const getProductConfiguration = createAsyncThunk(
  'dataMaintenance/getProductConfiguration',
  async (action: { page: number; limit: number; searchCode?: string }, thunkAPI) => {
    return ProductAssociationApi.getProductConfiguration(action, thunkAPI.dispatch);
  }
);

//To get Deleted Configuration paginated data
export const getArchivedConfiguration = createAsyncThunk(
  'dataMaintenance/getArchivedConfiguration',
  async (action: { page: number; limit: number; searchCode?: string }, thunkAPI) => {
    return ProductAssociationApi.getArchivedConfiguration(action, thunkAPI.dispatch);
  }
);

const dataMaintenanceSlice = createSlice({
  name: 'dataMaintenance',
  initialState,
  reducers: {
    resetCompareProductHierarchy(state) {
      state.compareProductHierarchy = initialState.compareProductHierarchy;
    },
    setUploadFile(state, action) {
      state.uploadFile.file = action.payload.file;
      state.uploadFile.importDropzone = action.payload.importDropzone;
    },
    removeUploadFile(state) {
      state.uploadFile.file = initialState.uploadFile.file;
      state.uploadFile.importDropzone = initialState.uploadFile.importDropzone;
    },
    setChunkFileUpload(state, action) {
      state.chunkUpload = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSalesText.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.salesText,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.SalesText
          );
        }
        return state;
      })
      .addCase(getImages.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.images,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.Images
          );
        }
        return state;
      })
      .addCase(getProductHierarchy.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.productHierarchy,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.ProductHierarchy
          );
        }
        return state;
      })
      .addCase(compareProductHierarchy.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.compareProductHierarchy,
            action.payload,
            !!action.payload.searchKey,
            EDataMaintenanceType.CompareProductHierarchy
          );
        }
        return state;
      })
      .addCase(getPrice.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.prices,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.Prices
          );
        }
        return state;
      })
      .addCase(getPriceListMap.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.priceListMap,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.PriceListMap
          );
        }
        return state;
      })
      .addCase(getCountryList.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          state.countries = action.payload.data;
        }
      })
      .addCase(getMarkets.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.markets,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.Markets
          );
        }
        return state;
      })
      .addCase(getMarketAssociation.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.marketAssociation,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.MarketAssociation
          );
        }
        return state;
      })
      .addCase(getLanguages.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.languages,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.Languages
          );
        }
        return state;
      })
      .addCase(getLanguageAssociation.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.languageAssociation,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.LanguageAssociation
          );
        }
        return state;
      })
      .addCase(getProductCatalog.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.productCatalog,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.ProductCatalog
          );
        }
        return state;
      })
      .addCase(getProductConfiguration.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.productConfiguration,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.ProductConfiguration
          );
        }
        return state;
      })
      .addCase(getArchivedConfiguration.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          return updateTableData(
            state.archivedConfiguration,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.ArchivedConfiguration
          );
        }
        return state;
      })
      .addCase(getProperties.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          action.payload.data = action.payload.data?.properties;
          return updateTableData(
            state.properties,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.Properties
          );
        }
        return state;
      })
      .addCase(getPropertyAssociations.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          action.payload.data = getProductPropertyAssociationRows(action.payload.data);
          return updateTableData(
            state.propertyAssociation,
            action.payload,
            !!action.payload.searchKey,
            EDataTableType.PropertyAssociation
          );
        }
        return state;
      })
      .addCase(getFeaturePropertyAssociations.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          const errorAction = action.payload.error.action as unknown as {
            searchCode: string;
            page: number;
          };
          const payload = {
            data: [],
            searchKey: errorAction.searchCode,
            page: errorAction.page,
            totalRecords: 1,
          };
          return updateFeaturePropertyAssociation(state.featurePropertyAssociation, payload);
        } else {
          action.payload.data = getFeaturePropertyRows(
            action.payload.data ? action.payload.data : [],
            action.payload.searchKey ?? ''
          );
          return updateFeaturePropertyAssociation(state.featurePropertyAssociation, action.payload);
        }
      })
      .addCase(getAllProperties.fulfilled, (state, action) => {
        if (!('error' in action.payload)) {
          state.propertyList = action.payload.data.properties;
        }
      })
      .addCase(getEnumListOfValues.fulfilled, (state, action) => {
        if (action.payload && !('error' in action.payload)) {
          state.enumListOfValues = action.payload.data;
        }
      });
  },
});

export const { resetCompareProductHierarchy, setUploadFile, removeUploadFile, setChunkFileUpload } =
  dataMaintenanceSlice.actions;

export default dataMaintenanceSlice.reducer;
